import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Interceptor que adiciona o cabeçalho X-User-Route com a rota atual do Angular
 * para requisições feitas às APIs do sistema (SaguiControl, SaguiEdu, SaguiLog).
 * 
 * O cabeçalho contém apenas a rota após #/ sem parâmetros.
 * Exemplo: Para URL "http://localhost:4200/#/financeiro/dre?id=123"
 * O cabeçalho será: X-User-Route: financeiro/dre
 */
@Injectable()
export class UserRouteInterceptor implements HttpInterceptor {

  private readonly apiUrls: string[] = [
    environment.webApiSaguiControlUrl,
    environment.webApiSaguiEduUrl,
    environment.webApiSaguiLogUrl
  ];

  constructor() { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    
    // Verificar se a requisição é para uma das APIs configuradas
    const isTargetApi = this.apiUrls.some(apiUrl => 
      apiUrl && request.url.startsWith(apiUrl)
    );

    if (!isTargetApi) {
      // Se não for para as APIs alvo, seguir sem modificar
      return next.handle(request);
    }

    // Extrair a rota atual do Angular (após #/)
    const userRoute = this.extractAngularRoute();

    if (!userRoute) {
      // Se não conseguir extrair a rota, seguir sem modificar
      return next.handle(request);
    }

    // Clonar a requisição e adicionar o cabeçalho X-User-Route
    const modifiedRequest = request.clone({
      setHeaders: {
        'X-User-Route': userRoute
      }
    });

    return next.handle(modifiedRequest);
  }

  /**
   * Extrai a rota do Angular a partir da URL do navegador.
   * Remove o hash (#/), parâmetros de query (?...) e fragmentos (#...).
   * 
   * Exemplos:
   * - "http://localhost:4200/#/financeiro/dre" -> "financeiro/dre"
   * - "http://localhost:4200/#/financeiro/dre?id=123" -> "financeiro/dre"
   * - "http://localhost:4200/#/financeiro/dre?id=123#section" -> "financeiro/dre"
   * 
   * @returns A rota limpa ou null se não conseguir extrair
   */
  private extractAngularRoute(): string | null {
    try {
      const fullUrl = window.location.href;
      
      // Verificar se tem hash (#)
      if (!fullUrl.includes('#/')) {
        return null;
      }

      // Extrair tudo após #/
      const afterHash = fullUrl.split('#/')[1];
      
      if (!afterHash) {
        return null;
      }

      // Remover parâmetros de query (?) e fragmentos (#)
      let route = afterHash.split('?')[0]; // Remove query params
      route = route.split('#')[0]; // Remove fragments
      
      // Remover trailing slash se existir
      route = route.replace(/\/$/, '');

      return route || null;
      
    } catch (error) {
      console.error('Erro ao extrair rota do Angular:', error);
      return null;
    }
  }
}
